import { ReactNode, useState } from 'react';
import {
  ComponentStyleConfig,
  ChakraProvider,
  extendTheme,
} from '@chakra-ui/react';

import { BASE_THEME_STYLES } from '../constants/constants';
import themeJSON from '../theme/theme.json';
import { ColorMode } from '../theme/modes.config';
import { ColorModeProvider } from './color-mode-context';

export interface AlleElementsComponentThemes {
  [componentName: string]: ComponentStyleConfig;
}

export interface AlleElementsProviderProps {
  /**
   * The `componentTheme` prop is used for adding Alle Element component themes
   * to the global theme. This means, if you are adding an Alle Element Package
   * to your application that has an exported theme, you will have to add that
   * package's theme to the `componentTheme` property in order for the component
   * to render properly. Not all Alle Element packages have a theme that needs
   * to be added to the `componentTheme` prop so please look at the package
   * documentation to see if there is a theme that is required to be added. An
   * example of how to use the `componentTheme` prop can be found [here](https://github.com/allergan-data-labs/alle-elements/blob/main/apps/docs/stories/helpers/ChakraWrapper.tsx)
   *
   */
  componentTheme?: AlleElementsComponentThemes;
  children: ReactNode;
  defaultColorMode?: ColorMode;
}

export const AlleElementsProvider = ({
  children,
  defaultColorMode = 'light',
  componentTheme = {},
}: AlleElementsProviderProps) => {
  const [colorMode, setColorMode] = useState(defaultColorMode);

  const theme = extendTheme(themeJSON, {
    ...BASE_THEME_STYLES,
    components: {
      ...componentTheme,
    },
  });

  return (
    <ChakraProvider resetCSS={false} theme={theme}>
      <ColorModeProvider value={{ colorMode, setColorMode }}>
        {children}
      </ColorModeProvider>
    </ChakraProvider>
  );
};
